import { useState, useContext } from "react";
import { AppBar, Toolbar, Button, Snackbar, Alert, Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "../../Helpers/apiClient";
import AuthContext from "../../Helpers/auth-context";
import { useQuote } from "./useQuote";

const Dynamics365NavigationBar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { dispatch } = useContext(AuthContext);
    const { getProductCount } = useQuote();

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const handleLogout = async () => {
        try {
            await apiClient.post("/api/Account/logout");

            setSnackbar({
                open: true,
                message: "Logout successful! Redirecting to homepage...",
                severity: "success",
            });

            setTimeout(() => {
                dispatch({ type: "LOGOUT" });
                navigate("/login", {
                    state: {
                        returnTo: location.pathname + location.search,
                    },
                });
            }, 2000);
        } catch (error) {
            setSnackbar({
                open: true,
                message: "Logout failed. Please try again.",
                severity: "error",
            });
        }
    };

    const handleButtonClick = (action) => {
        const queryParams = location.search;

        // Add 0.5 second delay for all navigation actions
        setTimeout(() => {
            switch (action) {
                case "logOut":
                    handleLogout();
                    break;

                case "cpq-home":
                    navigate(`/cpq-home${queryParams}`);
                    break;

                case "pump-station":
                    navigate(`/dynamics-pump-station${queryParams}`);
                    break;

                case "hydrovault":
                    navigate(`/dynamics-hydrovault${queryParams}`);
                    break;

                case "ecoprotector":
                    navigate(`/dynamics-ecoprotector${queryParams}`);
                    break;

                case "tornado-gpt-offline":
                    navigate(`/dynamics-tornado-gpt-offline${queryParams}`);
                    break;

                case "shopify-publisher":
                    navigate(`/ascora-shopify-publisher${queryParams}`);
                    break;

                case "setting":
                    navigate(`/cpq-setting${queryParams}`);
                    break;

                default:
                    break;
            }
        }, 500);
    };

    const getButtonStyles = (buttonType) => {
        const baseStyles = {
            minWidth: "140px",
            height: "40px",
            fontSize: "0.75rem",
            fontWeight: "bold",
        };

        switch (buttonType) {
            case "primary":
                return {
                    ...baseStyles,
                    color: "white",
                    backgroundColor: "#2e7d32",
                    "&:hover": {
                        backgroundColor: "#1b5e20",
                    },
                };

            case "danger":
                return {
                    ...baseStyles,
                    color: "white",
                    backgroundColor: "#d32f2f",
                    "&:hover": {
                        backgroundColor: "#b71c1c",
                    },
                };

            default:
                return {
                    ...baseStyles,
                    color: "white",
                    backgroundColor: "#54c9ec",
                    "&:hover": {
                        backgroundColor: "#3db5d9",
                    },
                };
        }
    };

    const buttons = [
        { label: "HYDROVAULT", action: "hydrovault", type: "default" },
        {
            label: "CPQ HOME",
            action: "cpq-home",
            type: "primary",
        },
        {
            label: "PUMP STATION",
            action: "pump-station",
            type: "default",
        },
        {
            label: "ECOPROTECTOR",
            action: "ecoprotector",
            type: "default",
        },
        {
            label: "Tornado GPT Offline",
            action: "tornado-gpt-offline",
            type: "default",
        },
        {
            label: "Shopify Publisher",
            action: "shopify-publisher",
            type: "primary",
        },
        {
            label: "Setting",
            action: "setting",
            type: "primary",
        },
        {
            label: "LOG OUT",
            action: "logOut",
            type: "danger",
        },
    ];

    return (
        <>
            <AppBar position="static" color="transparent" elevation={0} sx={{ width: "100%" }}>
                <Toolbar
                    sx={{
                        justifyContent: "center",
                        padding: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            justifyContent: "center",
                            maxWidth: "100%",
                        }}
                    >
                        {buttons.map((btn) => (
                            <Button
                                key={btn.action}
                                onClick={() => handleButtonClick(btn.action)}
                                sx={getButtonStyles(btn.type)}
                            >
                                {btn.label}

                                {btn.action === "cpq-home" && getProductCount() > 0 && (
                                    <span
                                        style={{
                                            marginLeft: "8px",
                                            fontSize: "0.7rem",
                                        }}
                                    >
                                        ({getProductCount()})
                                    </span>
                                )}
                            </Button>
                        ))}
                    </Box>
                </Toolbar>
            </AppBar>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() =>
                    setSnackbar({
                        ...snackbar,
                        open: false,
                    })
                }
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <Alert
                    onClose={() =>
                        setSnackbar({
                            ...snackbar,
                            open: false,
                        })
                    }
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Dynamics365NavigationBar;
